import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { CopyInput } from "@/components/copy-input";
import { useAppForm } from "@/components/form";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardFooter,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId/feed")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: channel } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.channels.updateFeedSettings.mutationOptions({
			onSuccess: async (_data, _payload, _result, context) => {
				await context.client.invalidateQueries({
					queryKey: orpc.channels.show.key({
						input: { channelId: params.channelId },
					}),
				});
				toast.success("Podcast feed settings updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this podcast feed settings.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			ItunesNewFeedUrl: channel.ItunesNewFeedUrl || "",
			RedirectUrl: channel.RedirectUrl || "",
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						ID: channel.ID,
						ItunesNewFeedUrl: value.ItunesNewFeedUrl.trim() || null,
						RedirectUrl: value.RedirectUrl.trim() || null,
					}),
				);

				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the podcast feed.";
				}
			},
		},
	});

	const feedUrl = `${import.meta.env.VITE_PODCAST_FEED_URL}/${channel.UrlTitle}`;

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Podcast Feed Settings</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="ItunesNewFeedUrl"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>iTunes new feed URL</field.FormLabel>
											<field.FormInput type="url" placeholder="https://example.com/new-feed.rss" />
											<field.FormDescription>
												Adds the iTunes new-feed-url value to the RSS feed.
											</field.FormDescription>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="RedirectUrl"
									children={(field) => (
										<field.FormItem className="lg:col-span-6">
											<field.FormLabel>RSS 301 redirect URL</field.FormLabel>
											<field.FormInput type="url" placeholder="https://example.com/feed.rss" />
											<field.FormDescription>
												Applies a permanent redirect for this podcast RSS feed.
											</field.FormDescription>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
						<FormCardFooter>
							<div className="flex w-full items-center gap-2">
								<form.FormErrorMessages />
								<form.FormButton size="xl" className="ml-auto">
									Update
								</form.FormButton>
							</div>
						</FormCardFooter>
					</FormCard>
					<FormCard className="col-span-12">
						<FormCardHeader>
							<FormCardTitle>Current RSS Feed</FormCardTitle>
						</FormCardHeader>
						<FormCardContent className="flex flex-col gap-4">
							<FieldGroup>
								<Field>
									<FieldLabel>Current feed URL</FieldLabel>
									<CopyInput value={feedUrl} readOnly type="url" />
									<FieldDescription>
										This is the public RSS feed URL for this podcast.
									</FieldDescription>
								</Field>
							</FieldGroup>
							<iframe
								src={feedUrl}
								title="RSS Feed"
								className="w-full rounded-lg border"
								style={{ height: "calc(100vh - 30rem)" }}
							/>
						</FormCardContent>
					</FormCard>
				</div>
			</form>
		</form.AppForm>
	);
}
